/**
 * Underline tabs. `subheader-height`, 2px `accent-solid` underline animated on
 * `duration-fast`, counts as a `caption` pill in `bg-active` (components.md §3).
 *
 * Keyboard follows the ARIA tabs pattern with a roving tabindex: one tab stop
 * for the whole set, arrows to move, Home/End to the ends. `activateOnFocus` is
 * off by default — the bottom tabs switch between Positions, Open Orders and
 * Fills, and arrowing through them while a market resolves would tear down and
 * rebuild three tables on the way past.
 */

import { useRef } from 'react'
import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

export interface TabItem<T extends string = string> {
  id: T
  label: ReactNode
  count?: number
  disabled?: boolean
  /** Pushes this tab, and any after it, to the far end of the bar. Order in
   *  the list is still the keyboard order. */
  end?: boolean
}

export interface TabsProps<T extends string = string> {
  items: readonly TabItem<T>[]
  value: T
  onChange: (id: T) => void
  activateOnFocus?: boolean
  /** `caption` for a panel header, `body` for the bottom tab bar. */
  size?: 'caption' | 'body'
  label: string
  className?: string
}

export function Tabs<T extends string = string>({
  items,
  value,
  onChange,
  activateOnFocus = false,
  size = 'body',
  label,
  className,
}: TabsProps<T>) {
  const refs = useRef(new Map<T, HTMLButtonElement>())

  const move = (from: number, step: number) => {
    const enabled = items.filter((i) => !i.disabled)
    if (enabled.length === 0) return
    const currentEnabledIndex = enabled.findIndex((i) => i.id === items[from]?.id)
    const next = enabled[(currentEnabledIndex + step + enabled.length) % enabled.length]
    if (!next) return
    refs.current.get(next.id)?.focus()
    if (activateOnFocus) onChange(next.id)
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cx(
        'flex h-[var(--ud-subheader-height)] items-stretch gap-4 border-b border-border-subtle',
        className,
      )}
    >
      {items.map((item, index) => {
        const selected = item.id === value
        return (
          <button
            key={item.id}
            ref={(el) => {
              if (el) refs.current.set(item.id, el)
              else refs.current.delete(item.id)
            }}
            role="tab"
            type="button"
            id={`tab-${item.id}`}
            aria-selected={selected}
            aria-controls={`tabpanel-${item.id}`}
            // Roving tabindex: the set is one tab stop, not one per tab.
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') move(index, 1)
              else if (e.key === 'ArrowLeft') move(index, -1)
              else if (e.key === 'Home') move(-1, 1)
              else if (e.key === 'End') move(0, -1)
              else return
              e.preventDefault()
            }}
            className={cx(
              'relative inline-flex items-center gap-1_5 whitespace-nowrap',
              item.end && 'ml-auto',
              // components.md §1's hit-target floor, below 768px only. Both axes:
              // `Trades` is 42px wide, which is tall enough and still a miss.
              'justify-center min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] md:min-h-0 md:min-w-0',
              size === 'caption' ? 'text-caption' : 'text-body',
              'transition-colors duration-[var(--ud-duration-fast)]',
              'focus-visible:shadow-focus focus-visible:outline-none',
              item.disabled
                ? 'cursor-not-allowed text-text-disabled'
                : selected
                  ? 'text-text-primary'
                  : 'text-text-secondary hover:text-text-primary',
            )}
          >
            {item.label}
            {item.count !== undefined ? (
              <span className="ud-num rounded-full bg-bg-active px-1 text-caption text-text-secondary">
                {item.count}
              </span>
            ) : null}
            <span
              aria-hidden="true"
              className={cx(
                'absolute inset-x-0 bottom-0 h-0_5 transition-opacity duration-[var(--ud-duration-fast)]',
                selected ? 'bg-accent-solid opacity-100' : 'opacity-0',
              )}
            />
          </button>
        )
      })}
    </div>
  )
}

/** The panel a tab controls. Separate so a tab bar can live in a different row of
 *  the grid from the content it switches — which is exactly the bottom tabs. */
export function TabPanel({
  id,
  active,
  children,
  className,
}: {
  id: string
  active: boolean
  children: ReactNode
  className?: string
}) {
  if (!active) return null
  return (
    <div role="tabpanel" id={`tabpanel-${id}`} aria-labelledby={`tab-${id}`} className={className}>
      {children}
    </div>
  )
}
